import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { supabase } from '../../supabaseClient';
import { generateHTML } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Image from '@tiptap/extension-image';
import Underline from '@tiptap/extension-underline';
import TextAlign from '@tiptap/extension-text-align';
import Link from '@tiptap/extension-link';
import Highlight from '@tiptap/extension-highlight';

const C = {
  blue: '#6699ff',
  navy: '#0b1120',
  cream: '#f5f0e8',
  creamLight: '#faf7f2',
  creamWarm: '#ede5d8',
  textDark: '#0d1520',
  textBody: '#3a4860',
  textMuted: '#6b7a92',
  white: '#ffffff',
};

export default function ArticleDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [article, setArticle] = useState(null);
  const [htmlContent, setHtmlContent] = useState('');
  const [files, setFiles] = useState([]);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    loadArticle();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  const loadArticle = async () => {
    const { data, error: fetchError } = await supabase
      .from('articles')
      .select('*')
      .eq('slug', slug)
      .eq('status', 'published')
      .single();

    if (fetchError || !data) {
      setNotFound(true);
      return;
    }

    setArticle(data);

    if (data.content) {
      try {
        const html = generateHTML(data.content, [
          StarterKit,
          Image,
          Underline,
          TextAlign.configure({ types: ['heading', 'paragraph'] }),
          Link,
          Highlight,
        ]);
        setHtmlContent(html);
      } catch (e) {
        setHtmlContent('<p>Error rendering content</p>');
      }
    }

    const { data: fileData } = await supabase
      .from('article_files')
      .select('*')
      .eq('article_id', data.id);

    if (fileData) setFiles(fileData);
  };

  const downloadFile = async (filePath, fileName) => {
    const { data } = await supabase.storage
      .from('article-files')
      .createSignedUrl(filePath, 60);

    if (data?.signedUrl) {
      const a = document.createElement('a');
      a.href = data.signedUrl;
      a.download = fileName;
      a.click();
    } else {
      alert('Download failed');
    }
  };

  if (notFound) {
    return (
      <div style={{
        minHeight: '100vh', background: C.navy, display: 'flex',
        alignItems: 'center', justifyContent: 'center', flexDirection: 'column',
        fontFamily: "'Manrope', sans-serif",
      }}>
        <h1 style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 48, color: C.cream, marginBottom: 16 }}>
          Article Not Found
        </h1>
        <p style={{ fontSize: 14, color: 'rgba(245,240,232,0.5)', marginBottom: 32 }}>
          This article may have been removed or doesn't exist.
        </p>
        <button
          onClick={() => navigate('/articles')}
          style={{
            padding: '14px 32px', background: C.blue, color: C.navy, border: 'none',
            fontFamily: "'Manrope', sans-serif", fontSize: 12, fontWeight: 700,
            letterSpacing: 2, textTransform: 'uppercase', cursor: 'pointer',
          }}
        >
          Browse Articles
        </button>
      </div>
    );
  }

  if (!article) {
    return (
      <div style={{
        minHeight: '100vh', background: C.creamLight, display: 'flex',
        alignItems: 'center', justifyContent: 'center',
        fontFamily: "'Manrope', sans-serif", color: C.textMuted,
      }}>
        Loading...
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', background: C.creamLight, fontFamily: "'Manrope', sans-serif" }}>
      {/* Nav */}
      <nav style={{
        position: 'fixed', top: 0, left: 0, right: 0, zIndex: 100,
        padding: '0 60px', height: 72,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        background: 'rgba(245,240,232,0.95)', backdropFilter: 'blur(24px)',
        borderBottom: `1px solid ${C.creamWarm}`,
      }}>
        <a href="/" style={{ textDecoration: 'none' }}>
          <span style={{ fontSize: 12, fontWeight: 700, letterSpacing: 3, textTransform: 'uppercase', color: C.navy }}>
            SPC <span style={{ color: C.blue }}>Insights</span>
          </span>
        </a>
        <div style={{ display: 'flex', alignItems: 'center', gap: 32 }}>
          <a href="/" style={{ fontSize: 12, fontWeight: 500, letterSpacing: 1.5, textTransform: 'uppercase', color: C.textBody, textDecoration: 'none' }}>Home</a>
          <a href="/articles" style={{ fontSize: 12, fontWeight: 500, letterSpacing: 1.5, textTransform: 'uppercase', color: C.textBody, textDecoration: 'none' }}>Articles</a>
        </div>
      </nav>

      {/* Cover Image */}
      {article.cover_image && (
        <div style={{ width: '100%', height: 440, overflow: 'hidden', marginTop: 72 }}>
          <img src={article.cover_image} alt={article.title} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
        </div>
      )}

      {/* Article */}
      <div style={{ maxWidth: 780, margin: '0 auto', padding: `${article.cover_image ? '48px' : '120px'} 24px 100px` }}>
        {/* Back link */}
        <button
          onClick={() => navigate('/articles')}
          style={{
            background: 'none', border: 'none', color: C.blue,
            fontSize: 12, fontWeight: 600, letterSpacing: 1.5, textTransform: 'uppercase',
            cursor: 'pointer', marginBottom: 32, padding: 0,
            fontFamily: "'Manrope', sans-serif",
          }}
        >
          ← All Articles
        </button>

        {/* Meta */}
        {article.category && (
          <span style={{
            fontSize: 10, fontWeight: 700, letterSpacing: 3, textTransform: 'uppercase',
            color: C.blue, display: 'block', marginBottom: 16,
          }}>
            {article.category}
          </span>
        )}

        <h1 style={{
          fontFamily: "'Cormorant Garamond', serif", fontSize: 48, fontWeight: 500,
          color: C.textDark, lineHeight: 1.15, marginBottom: 20,
        }}>
          {article.title}
        </h1>

        <div style={{ display: 'flex', alignItems: 'center', gap: 16, fontSize: 13, color: C.textMuted, marginBottom: 12 }}>
          <span>{article.author}</span>
          <span>·</span>
          <span>{new Date(article.published_at || article.created_at).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</span>
        </div>

        {article.tags?.length > 0 && (
          <div style={{ display: 'flex', gap: 8, marginBottom: 32 }}>
            {article.tags.map(tag => (
              <span key={tag} style={{
                padding: '4px 14px', background: 'rgba(102,153,255,0.06)',
                border: '1px solid rgba(102,153,255,0.12)',
                fontSize: 11, fontWeight: 600, color: C.blue,
              }}>
                {tag}
              </span>
            ))}
          </div>
        )}

        {/* Divider */}
        <div style={{ height: 1, background: C.creamWarm, marginBottom: 40 }} />

        {/* Body */}
        <div className="article-body" dangerouslySetInnerHTML={{ __html: htmlContent }} />

        {/* File Downloads */}
        {files.length > 0 && (
          <div style={{ marginTop: 48, paddingTop: 32, borderTop: `1px solid ${C.creamWarm}` }}>
            <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: 3, textTransform: 'uppercase', color: C.blue, marginBottom: 16 }}>
              Downloads
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {files.map(file => (
                <FileDownloadRow key={file.id} file={file} onDownload={downloadFile} />
              ))}
            </div>
          </div>
        )}

        {/* Back to articles */}
        <div style={{ marginTop: 60, paddingTop: 40, borderTop: `1px solid ${C.creamWarm}`, textAlign: 'center' }}>
          <button
            onClick={() => navigate('/articles')}
            style={{
              padding: '14px 36px', border: `1px solid ${C.navy}`, background: 'transparent',
              color: C.navy, fontFamily: "'Manrope', sans-serif", fontSize: 12,
              fontWeight: 600, letterSpacing: 2, textTransform: 'uppercase', cursor: 'pointer',
              transition: 'all 0.3s ease',
            }}
            onMouseEnter={e => { e.target.style.background = C.navy; e.target.style.color = C.cream; }}
            onMouseLeave={e => { e.target.style.background = 'transparent'; e.target.style.color = C.navy; }}
          >
            ← Browse All Articles
          </button>
        </div>
      </div>

      {/* Article body styles */}
      <style>{`
        .article-body { font-size: 16px; line-height: 1.85; color: ${C.textBody}; }
        .article-body h1 { font-family: 'Cormorant Garamond', serif; font-size: 32px; font-weight: 600; margin: 32px 0 16px; color: ${C.navy}; }
        .article-body h2 { font-family: 'Cormorant Garamond', serif; font-size: 26px; font-weight: 600; margin: 28px 0 14px; color: ${C.navy}; }
        .article-body h3 { font-family: 'Cormorant Garamond', serif; font-size: 22px; font-weight: 600; margin: 24px 0 10px; color: ${C.navy}; }
        .article-body p { margin: 0 0 16px; }
        .article-body ul, .article-body ol { padding-left: 24px; margin: 0 0 16px; }
        .article-body li { margin: 6px 0; }
        .article-body blockquote { border-left: 3px solid ${C.blue}; padding-left: 20px; margin: 20px 0; color: ${C.textBody}; font-style: italic; }
        .article-body img { max-width: 100%; height: auto; margin: 20px 0; border: 1px solid ${C.creamWarm}; }
        .article-body a { color: ${C.blue}; text-decoration: underline; }
        .article-body hr { border: none; border-top: 1px solid ${C.creamWarm}; margin: 32px 0; }
        .article-body mark { background: rgba(102,153,255,0.2); padding: 2px 6px; }
      `}</style>
    </div>
  );
}

function FileDownloadRow({ file, onDownload }) {
  const [hovered, setHovered] = useState(false);

  const formatSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  return (
    <div
      onClick={() => onDownload(file.file_path, file.file_name)}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '14px 20px', background: hovered ? C.white : C.creamLight,
        border: `1px solid ${hovered ? C.blue : C.creamWarm}`,
        cursor: 'pointer', transition: 'all 0.2s ease',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <span style={{
          padding: '3px 10px', background: 'rgba(102,153,255,0.08)',
          fontSize: 10, fontWeight: 700, color: C.blue, letterSpacing: 1,
          textTransform: 'uppercase',
        }}>
          {file.file_name.split('.').pop()}
        </span>
        <span style={{ fontSize: 14, color: C.textDark, fontWeight: 500 }}>{file.file_name}</span>
        <span style={{ fontSize: 11, color: C.textMuted }}>{formatSize(file.file_size)}</span>
      </div>
      <span style={{ fontSize: 11, fontWeight: 600, color: C.blue, letterSpacing: 1, textTransform: 'uppercase' }}>
        Download
      </span>
    </div>
  );
}